import { useEffect, useMemo, useState } from "react"
import { DynamicKpiCard } from "components/ui/dynamic-kpi-card"
import type { WidgetProps } from "lib/widget-types"
import { UserAvatar } from "components/ui/avatar"
import { getAllKpis, type KpiData, type KpiDefinition } from "lib/kpi-registry"
import "lib/kpi-registrations"
import { useUser } from "contexts/UserContext"

interface WelcomeKpiConfig {
  userName?: string
  userFirstName?: string
  userLastName?: string
  userUsername?: string
  userProfilePhotoUrl?: string | null
  date?: string
}

export function WelcomeKpiWidget({ widget }: WidgetProps) {
  const config = (widget.config as WelcomeKpiConfig) || {}
  const userName = config.userName || "Utente"
  const userFirstName = config.userFirstName
  const userLastName = config.userLastName
  const userUsername = config.userUsername
  const userProfilePhotoUrl = config.userProfilePhotoUrl
  const dateStr = config.date || new Date().toISOString()

  const { user } = useUser()
  const enabledModules = (user as any)?.enabled_modules as Record<string, boolean> | undefined

  const kpiDefs = useMemo<KpiDefinition[]>(() => {
    return getAllKpis().filter((def) => {
      if (!def.moduleName) return true
      return enabledModules?.[def.moduleName] === true
    })
  }, [enabledModules])

  const [kpiData, setKpiData] = useState<Record<string, KpiData>>({})
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    const fetchKpiData = async () => {
      try {
        const results = await Promise.all(
          kpiDefs.map(async (def) => {
            try {
              const data = await def.fetchData()
              return [def.id, data] as const
            } catch {
              return [def.id, { value: 0 } as KpiData] as const
            }
          })
        )
        if (cancelled) return
        const map: Record<string, KpiData> = {}
        for (const [id, data] of results) map[id] = data
        setKpiData(map)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    fetchKpiData()
    return () => {
      cancelled = true
    }
  }, [kpiDefs])

  // Format date in Italian
  const date = new Date(dateStr)
  const formatter = new Intl.DateTimeFormat("it-IT", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  })
  const formattedDate = formatter.format(date)
  const capitalizedDate = formattedDate.charAt(0).toUpperCase() + formattedDate.slice(1)

  return (
    <div
      className="rounded-xl backdrop-blur-sm p-4 border hexa-card-shadow h-full"
      style={{
        backgroundColor: "var(--hexa-card-bg)",
        borderColor: "var(--hexa-border)",
      }}
    >
      <div className="flex items-center gap-2.5 mb-4">
        {/* User Avatar - shows initials with color based on name */}
        <UserAvatar
          firstName={userFirstName}
          lastName={userLastName}
          username={userUsername || userName}
          imageUrl={userProfilePhotoUrl || undefined}
          size="md"
          className="shadow-lg"
        />
        <div className="min-w-0 break-words">
          <h2 className="text-xl md:text-2xl font-semibold" style={{ color: "var(--hexa-text-primary)" }}>
            Buongiorno, {userName}!
          </h2>
          <p className="text-xs font-normal" style={{ color: "var(--hexa-text-secondary)" }}>
            {capitalizedDate}
          </p>
        </div>
      </div>

      <div
        className="grid grid-cols-[repeat(auto-fit,minmax(110px,1fr))] gap-2.5"
      >
        {kpiDefs.map((def) => (
          <DynamicKpiCard
            key={def.id}
            definition={def}
            data={kpiData[def.id]}
            loading={loading}
          />
        ))}
      </div>
    </div>
  )
}
